import React, { useEffect, useRef, useState } from 'react';
import {BrowserRouter as Router, useLocation} from 'react-router-dom';
import { ConfigProvider, Modal } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import Header from "./components/Header";
import ErrorBoundary from "./components/ErrorBoundary";
import CustomerServiceWidget from "./components/CustomerService";
import UserFeedbackWidget from "./components/UserFeedback";
import { FloatingActionsProvider } from "./components/FloatingActions";
import { UserInfoProvider } from './contexts/UserInfoContext';
import AppRoutes from "./routes";
import Monitor from "./pages/Monitor";
import './i18n';
import { useTranslation } from './hooks/useTranslation';
import { getImgCloudBasename } from './utils/appRouting';
import { fetchTestDomainStatus } from './utils/testDomain';

function App() {
  const { currentLanguage, t } = useTranslation();
  const [locale, setLocale] = useState(currentLanguage === 'zh' ? zhCN : enUS);
  const testDomainWarningShownRef = useRef(false);

  useEffect(() => {
    // 根据当前语言设置 Ant Design 的 locale
    setLocale(currentLanguage === 'zh' ? zhCN : enUS);
  }, [currentLanguage]);

  useEffect(() => {
    let active = true;
    fetchTestDomainStatus(window.location.hostname)
      .then(({ isTestDomain, mainDomainUrl }) => {
        if (!active || testDomainWarningShownRef.current || !isTestDomain) {
          return;
        }
        testDomainWarningShownRef.current = true;

        Modal.warning({
          title: t('testDomainWarning.title'),
          content: (
            <>
              <p>{t('testDomainWarning.description', { hostname: window.location.hostname })}</p>
              <p>
                <a href={mainDomainUrl}>{mainDomainUrl}</a>
              </p>
            </>
          ),
          okText: t('testDomainWarning.goToMainDomain'),
          closable: true,
          onOk: () => {
            window.location.href = mainDomainUrl;
          },
        });
      })
      .catch((error) => console.warn('Failed to load test-domain status:', error));

    return () => {
      active = false;
    };
  }, [t]);

  useEffect(() => {
    const isLocalhost = /^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname);
    if (window.location.protocol !== 'https:' && !isLocalhost) {
      const httpsUrl = window.location.href.replace(/^http:/, 'https:');
      Modal.warning({
        title: t('httpsWarning.title'),
        content: (
          <>
            <p>{t('httpsWarning.description')}</p>
            <p>
              <a
                href={httpsUrl}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = httpsUrl;
                }}
              >
                {httpsUrl}
              </a>
            </p>
          </>
        ),
        okText: t('httpsWarning.goToHttps'),
        onOk: () => {
          window.location.href = httpsUrl;
        },
      });
    }
  }, [t]);

  return (
    <ConfigProvider locale={locale}>
      <Router basename={getImgCloudBasename() || undefined}>
        <FloatingActionsProvider>
          <AppShell />
        </FloatingActionsProvider>
      </Router>
    </ConfigProvider>
  );
}

export const AppShell: React.FC = () => {
  const location = useLocation();
  const isDirectoryShare = location.pathname === '/share/d' || location.pathname.startsWith('/share/d/');
  const isAiInfo = /^\/ai-info\/?$/.test(location.pathname);
  const isStandaloneRoute = isDirectoryShare || isAiInfo;

  const shell = (
    <div className="App">
      {!isStandaloneRoute && (
        <ErrorBoundary>
          <Header />
        </ErrorBoundary>
      )}

      <main style={{ minHeight: isStandaloneRoute ? '100vh' : 'calc(100vh - 56px)' }}>
        <AppRoutes />
      </main>

      {!isStandaloneRoute && (
        <>
          <ErrorBoundary>
            <Monitor/>
          </ErrorBoundary>
          <ErrorBoundary>
            <CustomerServiceWidget />
          </ErrorBoundary>
          <ErrorBoundary>
            <UserFeedbackWidget />
          </ErrorBoundary>
        </>
      )}
    </div>
  );

  return isStandaloneRoute ? shell : (
    <UserInfoProvider>
      {shell}
    </UserInfoProvider>
  );
};

export default App;
